import { cn, Table, Tooltip, Pagination, EmptyState } from '@heroui/react';
import { getLocalTimeZone } from '@internationalized/date';
import { TrashSimpleIcon, ArrowsClockwiseIcon } from '@phosphor-icons/react';
import pluralize from 'pluralize';
import React from 'react';
import { useDateFormatter } from 'react-aria';
import { Link, useFetcher } from 'react-router';

import { TraitChip, CustomButton, AddHabitDialogButton } from '@components';
import { useHabits } from '@hooks';
import type { Habit, HabitStats, HabitDetailsSubmission } from '@models';
import { useConfirmationActions } from '@stores';

import habitColumns from './habit-columns';
import HabitIcon from './HabitIcon';
import HabitLastEntry from './HabitLastEntry';
import HabitLongestStreak from './HabitLongestStreak';
import HabitTotalEntries from './HabitTotalEntries';

const ROWS_PER_PAGE = 10;

type HabitsTableProps = {
  habitsStats: Record<Habit['id'], HabitStats>;
};

const HabitsTable = ({ habitsStats }: HabitsTableProps) => {
  const [page, setPage] = React.useState(1);
  const dateFormatter = useDateFormatter({
    day: 'numeric',
    month: 'short',
    timeZone: getLocalTimeZone(),
    year: 'numeric',
  });

  const habits = useHabits();
  const deleteFetcher = useFetcher();
  const isDeleting = deleteFetcher.state !== 'idle';
  const { askConfirmation } = useConfirmationActions();

  const handleDelete = async (habit: Habit) => {
    if (isDeleting) {
      return;
    }

    const confirmed = await askConfirmation({
      title: 'Delete habit',
      variant: 'danger',
      description: (
        <div>
          Are you sure you want to delete <strong>{habit.name}</strong> habit?
          <br />
          <br />
          <i className="text-sm">
            This action deletes all related calendar entries and can&apos;t be
            undone
          </i>
        </div>
      ),
    });

    if (confirmed) {
      void deleteFetcher.submit(
        { intent: 'delete-habit' } satisfies HabitDetailsSubmission,
        {
          action: `/habits/${habit.id}`,
          encType: 'application/json',
          method: 'post',
        }
      );
    }
  };

  const habitsList = Object.values(habits);

  const totalPages = Math.ceil(habitsList.length / ROWS_PER_PAGE);

  const pages = Array.from({ length: totalPages }, (_, i) => {
    return i + 1;
  });

  const paginatedHabits = React.useMemo(() => {
    const start = (page - 1) * ROWS_PER_PAGE;

    return habitsList.slice(start, start + ROWS_PER_PAGE);
  }, [page, habitsList]);

  const start = (page - 1) * ROWS_PER_PAGE + 1;
  const end = Math.min(page * ROWS_PER_PAGE, habitsList.length);

  return (
    <div className="w-full space-y-4 px-8 pt-4 pb-2 lg:px-16">
      <div className="space-y-1">
        <div className="flex w-full items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-300">
            Your habits
          </h1>

          {!!habitsList.length && <AddHabitDialogButton />}
        </div>

        {!!habitsList.length && (
          <p className="text- text-sm">
            You&apos;re tracking {pluralize('habit', habitsList.length, true)}
          </p>
        )}
      </div>

      <Table aria-label="Habits tracking table">
        <Table.ScrollContainer>
          <Table.Content>
            <Table.Header columns={habitColumns}>
              {(column) => {
                return (
                  <Table.Column
                    key={column.key}
                    aria-label={column.label}
                    isRowHeader={column.key === 'icon'}
                    className={cn(
                      column.key === 'actions' && 'text-right',
                      ['icon', 'total-entries'].includes(column.key) &&
                        'text-center'
                    )}
                  >
                    {column.label}
                  </Table.Column>
                );
              }}
            </Table.Header>
            <Table.Body
              items={paginatedHabits}
              aria-label="Habits data"
              renderEmptyState={() => {
                return (
                  <EmptyState className="flex h-full w-full flex-col items-center justify-center gap-4 py-8 text-center">
                    <div className="flex items-center gap-2">
                      <ArrowsClockwiseIcon className="text-muted size-4" />
                      <span className="text-muted text-xs">No habits yet</span>
                    </div>
                    <p className="text-foreground text-sm">
                      Add your first habit to get started!
                    </p>
                    <AddHabitDialogButton />
                  </EmptyState>
                );
              }}
            >
              {paginatedHabits.map((habit) => {
                const habitStats = habitsStats[habit.id] || {};

                return (
                  <Table.Row
                    key={habit.id}
                    className="hover:bg-background"
                    aria-labelledby={`habit-name-${habit.id}`}
                  >
                    <Table.Collection>
                      <Table.Cell className="w-10 rounded-l-md">
                        <HabitIcon habit={habit} />
                      </Table.Cell>
                      <Table.Cell>
                        <Link
                          className="font-semibold"
                          to={`/habits/${habit.id}`}
                          id={`habit-name-${habit.id}`}
                        >
                          {habit.name}
                        </Link>
                        {habit.description && (
                          <p className="text-left text-xs">
                            <i>{habit.description}</i>
                          </p>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <TraitChip trait={habit.trait} />
                      </Table.Cell>
                      <Table.Cell>
                        {dateFormatter.format(new Date(habit.createdAt))}
                      </Table.Cell>
                      <Table.Cell>
                        <HabitLastEntry
                          timestamp={Number(
                            new Date(habitStats.lastEntryAt || 0)
                          )}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <HabitLongestStreak
                          streak={{
                            streakEnd: habitStats.longestStreakEnd,
                            streakLength: habitStats.longestStreakLength,
                            streakStart: habitStats.longestStreakStart,
                          }}
                        />
                      </Table.Cell>
                      <Table.Cell
                        className="text-center"
                        aria-label={`Total entries for ${habit.name}`}
                      >
                        <HabitTotalEntries count={habitStats.totalEntries} />
                      </Table.Cell>
                      <Table.Cell aria-label="Actions" className="rounded-r-md">
                        <div
                          role="group"
                          className="flex justify-end gap-2"
                          aria-label={`Actions for habit ${habit.name}`}
                        >
                          <Tooltip closeDelay={0}>
                            <Tooltip.Trigger>
                              <CustomButton
                                size="sm"
                                isIconOnly
                                variant="ghost"
                                isDisabled={isDeleting}
                                className="group text-danger"
                                aria-label={`Delete habit: ${habit.name}`}
                                aria-describedby={`delete-tooltip-${habit.id}`}
                                data-testid={`delete-habit-id-${habit.id}-button`}
                                onPress={() => {
                                  return handleDelete(habit);
                                }}
                              >
                                <TrashSimpleIcon
                                  size={16}
                                  weight="bold"
                                  aria-hidden="true"
                                />
                              </CustomButton>
                            </Tooltip.Trigger>
                            <Tooltip.Content>Delete habit</Tooltip.Content>
                          </Tooltip>
                        </div>
                      </Table.Cell>
                    </Table.Collection>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Content>
        </Table.ScrollContainer>
        {!!totalPages && (
          <Table.Footer>
            <Pagination size="sm">
              <Pagination.Summary>
                {start} to {end} of {habitsList.length} results
              </Pagination.Summary>
              <Pagination.Content>
                <Pagination.Item>
                  <Pagination.Previous
                    isDisabled={page === 1}
                    onPress={() => {
                      return setPage((p) => {
                        return Math.max(1, p - 1);
                      });
                    }}
                  >
                    <Pagination.PreviousIcon />
                    Prev
                  </Pagination.Previous>
                </Pagination.Item>
                {pages.map((p) => {
                  return (
                    <Pagination.Item key={p}>
                      <Pagination.Link
                        isActive={p === page}
                        className={cn(p === page && 'bg-accent text-white')}
                        onPress={() => {
                          return setPage(p);
                        }}
                      >
                        {p}
                      </Pagination.Link>
                    </Pagination.Item>
                  );
                })}
                <Pagination.Item>
                  <Pagination.Next
                    isDisabled={page === totalPages}
                    onPress={() => {
                      return setPage((p) => {
                        return Math.min(totalPages, p + 1);
                      });
                    }}
                  >
                    Next
                    <Pagination.NextIcon />
                  </Pagination.Next>
                </Pagination.Item>
              </Pagination.Content>
            </Pagination>
          </Table.Footer>
        )}
      </Table>
    </div>
  );
};

export default HabitsTable;
